import { useCustomers } from "@/hooks/Customer";
import { Customer } from "@/services/api";
import { Combobox, Option, Spinner, Label } from "@fluentui/react-components";
import * as React from "react";
import { useInView } from "react-hook-inview";

export interface ICustomerPickerProps {
	selectedCustomerId?: string | null;
	onSelectedCustomerChange?: (id?: string | null) => void;
}
const SHOWCOUNT = 30;
export function CustomerPicker(props: ICustomerPickerProps) {
	const { data: customers, isLoading } = useCustomers();

	const [ddIsOpen, setDDIsOpen] = React.useState(false);
	const [node, inView] = useInView();
	const [customSearch, setCustomSearch] = React.useState<string>("");
	const [selectedCustomer, setSelectedCustomer] = React.useState<Customer | null | undefined>();
	const [showCount, setShowCount] = React.useState(SHOWCOUNT);

	const filteredCustomers = React.useMemo(() => {
		if (!customSearch || !customers?.length) return customers ?? [];

		let visibleCustomers = customers.filter(
			(i) => (i.name?.toLocaleLowerCase().indexOf(customSearch.toLocaleLowerCase()) ?? -1) > -1
		);
		return visibleCustomers;
	}, [customers, customSearch]);

	const visibleCustomers = React.useMemo(() => {
		const visibleCustomers = filteredCustomers?.slice(0, showCount) ?? [];
		return visibleCustomers;
	}, [filteredCustomers, showCount]);

	React.useEffect(() => {
		if (ddIsOpen && inView) {
			setShowCount(() => showCount + SHOWCOUNT);
		} else if (!ddIsOpen) {
			setShowCount(SHOWCOUNT);
		}
	}, [ddIsOpen, inView]);

	React.useEffect(() => {
		if (!!props.selectedCustomerId) {
			setSelectedCustomer(customers?.find((i) => i.id === props.selectedCustomerId));
		}
	}, [customers, props.selectedCustomerId]);

	return (
		<div>
			<Combobox
				disabled={isLoading && visibleCustomers.length === 0}
				placeholder={isLoading && visibleCustomers.length === 0 ? "Loading..." : "Select a store"}
				value={!!customSearch ? customSearch : selectedCustomer?.name ?? ""}
				onChange={(ev) => {
					const value = ev.target.value;
					setCustomSearch(value);
				}}
				onOpenChange={(ev, ctx) => {
					setDDIsOpen(ctx.open);
					setCustomSearch("");
				}}
				defaultSelectedOptions={[]}
				onOptionSelect={(ev, opts) => {
					const selection = opts.selectedOptions[0];
					const match = customers?.find((i) => i.id === selection);
					setSelectedCustomer(match);
					if (props.onSelectedCustomerChange) props.onSelectedCustomerChange(selection);
				}}
				selectedOptions={!!selectedCustomer?.id ? [selectedCustomer.id] : []}>
				{visibleCustomers.map((i) => (
					<Option key={i.id!} value={i.id!} text={i.name || ""}>
						{i.name}
					</Option>
				))}
				{filteredCustomers.length !== visibleCustomers.length && (
					<Option disabled value="loading..." text="Loading..." ref={node}>
						<Spinner label={"Loading..."} size="small" />
					</Option>
				)}
			</Combobox>
		</div>
	);
}
